import { Component, OnInit, Input } from '@angular/core';
import { OccupationInformation } from './occupation-information.model';
import { FormGroup, FormBuilder } from '@angular/forms';
import { NameValueDto } from '@ig-core/interfaces/name-value-dto';
import { ReferenceCodeService } from '@app/admin/reference-code/reference-code.service';
import { ApplicationService } from '@app/applications/application.service';
import { DateAdapter, MatSnackBar } from '@angular/material';
import { LoanApplication, ApplicationAccessSpecifier } from '@app/applications/applications.model';
import { ValidationService } from '../../../application-validators/validation.service';

@Component({
  selector: 'eng-occupation-information',
  templateUrl: 'occupation-information.template.html',
  styleUrls: ['../../application-details.styles.scss']
})
export class OccupationInformationComponent implements OnInit {

  @Input() application: LoanApplication;
  @Input() occupation: OccupationInformation;
  @Input() menuCode: string;

  occupationForm: FormGroup;
  occupationInformation: OccupationInformation;
  categories: NameValueDto[];
  companyTypes: NameValueDto[];
  salaryCreditModes: NameValueDto[];

  isFormDisabled: boolean;
  showForm: boolean;
  allowAccess: boolean;
  menuItemAllowAccess: boolean;
  cfaFinalized: boolean = false;

  applicationAccessSpecifiers: ApplicationAccessSpecifier[];
  menuItemAccessSpecifier: ApplicationAccessSpecifier;

  constructor(
    private formBuilder: FormBuilder,
    private referenceCodeService: ReferenceCodeService,
    private applicationService: ApplicationService,
    private _snackbar: MatSnackBar,
    private dateAdapter: DateAdapter<Date>,
    private validationService: ValidationService
  ) {
    this.dateAdapter.setLocale('en-IN');
    this.allowAccess = this.applicationService.allowAccess;
  }

  ngOnInit(): void {
    this.isFormDisabled = true;
    this.getRefCodes();
    this.applyValidatorsToOccupationForm();
    this.getMenuItemAccess();
    this.checkCfaStatus();
  }

  applyValidatorsToOccupationForm() {
    this.occupationForm = this.formBuilder.group({
      category: '',
      companyType: '',
      companyName: '',
      companyAddress: '',
      companyLandmark: '',
      distanceFromBranch: null,
      designation: '',
      totalExperience: null,
      currentCompanyExperience: null,
      epfoNumber: '',
      grossMonthlyIncome: null,
      salaryCreditMode: '',
      netMonthlyIncome: null
    });

    this.validationService.applyValidationRules(this.occupationForm, 'OccupationInformation')
      .then(() => {
        this.fetchOccupationInformation();
      })
      .catch(() => { });
  }

  getRefCodes() {
    this.referenceCodeService.getShortRefCodes('occupation_type')
      .subscribe((response: any) => this.categories = response.occupation_type);

    this.referenceCodeService.getShortRefCodes('company_type')
      .subscribe((response: any) => this.companyTypes = response.company_type);

    this.referenceCodeService.getShortRefCodes('salary_credit_mode')
      .subscribe((response: any) => this.salaryCreditModes = response.salary_credit_mode);
  }

  fetchOccupationInformation() {
    this.showForm = false;
    this.applicationService.getOccupationProfile(this.application.uid, this.occupation.uid)
      .subscribe((response: any) => {
        if (response && response.body) {
          const occupationInformation: OccupationInformation = response.body;
          this.occupationInformation = occupationInformation;
          this.showForm = true;
          this.buildOccupationForm(occupationInformation);
        }
      });
  }

  buildOccupationForm(occupationInformation: OccupationInformation) {
    this.occupationForm.patchValue({
      category: occupationInformation.category,
      companyType: occupationInformation.companyType,
      companyName: occupationInformation.companyName,
      companyAddress: occupationInformation.companyAddress,
      companyLandmark: occupationInformation.companyLandmark,
      distanceFromBranch: occupationInformation.distanceFromBranch,
      designation: occupationInformation.designation,
      totalExperience: occupationInformation.totalExperience,
      currentCompanyExperience: occupationInformation.currentCompanyExperience,
      grossMonthlyIncome: occupationInformation.grossMonthlyIncome,
      netMonthlyIncome: occupationInformation.netMonthlyIncome,
      salaryCreditMode: occupationInformation.salaryCreditMode,
      epfoNumber: occupationInformation.epfoNumber
    });

    this.occupationForm.disable();
    this.isFormDisabled = true;
  }

  saveOccupationInformation() {
    this.validationService.markFormGroupTouched(this.occupationForm);

    if (!this.occupationForm.valid) return;

    const formFields = this.occupationForm.getRawValue();
    this.occupationInformation.category = formFields.category;
    this.occupationInformation.companyType = formFields.companyType;
    this.occupationInformation.companyName = formFields.companyName;
    this.occupationInformation.companyAddress = formFields.companyAddress;
    this.occupationInformation.companyLandmark = formFields.companyLandmark;
    this.occupationInformation.distanceFromBranch = formFields.distanceFromBranch;
    this.occupationInformation.designation = formFields.designation;
    this.occupationInformation.totalExperience = formFields.totalExperience;
    this.occupationInformation.currentCompanyExperience = formFields.currentCompanyExperience;
    this.occupationInformation.grossMonthlyIncome = formFields.grossMonthlyIncome;
    this.occupationInformation.netMonthlyIncome = formFields.netMonthlyIncome;
    this.occupationInformation.salaryCreditMode = formFields.salaryCreditMode;
    this.occupationInformation.epfoNumber = formFields.epfoNumber;

    this.applicationService.saveOccupationInformation(this.occupationInformation, this.application.uid, this.occupation.uid)
      .toPromise()
      .then((response: any) => {
        this.fetchOccupationInformation();
        this._snackbar.open('Occupation information updated successfully', 'Close', { duration: 2000 });
      })
      .catch((failure) => {
        this.applicationService.displayErrorMessages(['Occupation information update failed', failure]);
        console.log(failure);
      });

  }

  checkCfaStatus() {
    this.applicationService.getCfaStatus(this.application.uid)
      .subscribe(response => {
        this.cfaFinalized = response && response.body ? response.body.cfaFinalized : false;
      });
  }

  applyCfaRestrictions() {
    if (!this.occupationForm) return;

    if (this.cfaFinalized) {
      const grossCtrl = this.occupationForm.get('grossMonthlyIncome');
      const netCtrl = this.occupationForm.get('netMonthlyIncome');

      if (grossCtrl) grossCtrl.disable();
      if (netCtrl) netCtrl.disable();
    }
  }

  enableDisableForm() {
    this.isFormDisabled = false;
    this.occupationForm.enable();
    this.applyCfaRestrictions();
  }

  cancelForm() {
    this.buildOccupationForm(this.occupationInformation);
    this.isFormDisabled = true;
  }

  getMenuItemAccess() {
    this.applicationService.getApplicationAccessModifiers(this.application.uid)
      .subscribe((response) => {
        this.applicationAccessSpecifiers = response.body;
        this.menuItemAccessSpecifier = this.applicationAccessSpecifiers
          .find(accessSpecifier => accessSpecifier.category === this.menuCode);
        if (this.menuItemAccessSpecifier) {
          this.menuItemAllowAccess = this.menuItemAccessSpecifier.allowAccess;
        }
      });
  }
}
